import {AutogenLayoutDriverCallins} from "./autogen/AutogenLayoutDriverCallins";
import {getDispatchControlValue, LMEUserSettings} from "@mcleod/general";
import {Api, DateUtil, Model, ModelRow, ModelSearchResult, PermissionsUtil, StringUtil} from "@mcleod/core";
import {
    ClickEvent, Label,
    LookupModelSelectionEvent,
    TableRowCreationEvent,
    TableRowDisplayEvent,
    Textbox
} from "@mcleod/components";
import {PlanningUtil} from "../planning/PlanningUtil";

interface DriverCallinsProps {
    driverId: string;
    tractorId: string;
    nextStop: ModelRow;
}

export class DriverCallins extends AutogenLayoutDriverCallins {

    // Properties - Passed
    private driverId: string;
    private tractorId: string;
    private nextStop: ModelRow;

    private textboxLastLocation: Textbox;

    // Properties - Fetched
    private orderId: string;
    private movementId: string;
    private movementStatus: string;
    private hasTemperatures: boolean = false;
    private setpointTemp: number;
    private returnTemp: number;
    
    public initialize(props: DriverCallinsProps, currentLocation?: Textbox) {
        this.driverId = props.driverId;
        this.tractorId = props.tractorId;
        this.nextStop = props.nextStop;
        this.textboxLastLocation = currentLocation;

        if (PermissionsUtil.isUserDeniedLayout(this.layoutName)) return;
        this.addLayoutLoadListener(event => this.initializeColumnsAndData());
    }

    private async initializeColumnsAndData() {
        this.mainDataSource.search({driver_id: this.driverId, call_date_time: ">=t-10"});
        await this.sourceDriverOrder.search({driver_id: this.driverId}).then((result: ModelSearchResult) => {
            if (result != null && result.modelRows.length > 0) {
                const data = result.getSingleModelRow();
                this.orderId = data.get("order_id");
                this.movementId = data.get("movement_id");
                this.movementStatus = data.get("movement_status");
                this.hasTemperatures = !data.isNull("temp_max") && !data.isNull("temp_min");
                if (this.hasTemperatures) {
                    // Order has temperature requirements
                    this.setpointTemp = data.get("temp_setpoint");
                    this.returnTemp = data.get("temp_return");
                } else {
                    this.removeTemperatureColumns();
                }
            } else {
                this.removeTemperatureColumns();
            }
        });
        this.fillQuickAddRow(this.tableDriverCallins.quickAddData);
    }

    private fillQuickAddRow(modelRow: ModelRow) {
        if (modelRow == null) return;
        Model.searchSingleRecord("common/users", {id: LMEUserSettings.getUserId()}, "common/UserLookup").then((userModelRow: ModelRow) => {
            modelRow.setLookupModelData("entered_by", userModelRow);
        });
        modelRow.set("driver_id", this.driverId);
        if (this.nextStop) {
            modelRow.set("miles2consignee", this.nextStop.get("move_dist_from_previous"));
            modelRow.set("new_eta", this.nextStop.get("eta"));
        }
        modelRow.set("order_id", this.orderId);
        modelRow.set("movement_id", this.movementId);
        modelRow.set("movement_status", this.movementStatus);
        modelRow.set("tractor_id", this.tractorId);
        if (this.hasTemperatures) {
            // Order has temperature requirements
            modelRow.set("setpoint_temp", this.setpointTemp);
            modelRow.set("temperature", this.returnTemp);
        }
        this.tableDriverCallins.quickAddRow.displayComponentValues();
    }

    private removeTemperatureColumns() {
        this.tableDriverCallins.removeColumn("columnHeaderSetpointTemp");
        this.tableDriverCallins.removeColumn("columnHeaderReturnTemp");
    }

    private textboxCityNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selection: ModelRow = event.selection;
        const quickAddRow: ModelRow = this.tableDriverCallins.quickAddData;
        if (selection) {
            quickAddRow.set("city_id", selection.get("id"));
            quickAddRow.set("city_name", selection.get("name"));
            quickAddRow.set("state", selection.get("state_id"));
            quickAddRow.set("latitude", selection.get("latitude"));
            quickAddRow.set("longitude", selection.get("longitude"));
            quickAddRow.set("zip_code", selection.get("zip_code"));
        } else {
            quickAddRow.set("city_id", null);
            quickAddRow.set("city_name", null);
            quickAddRow.set("state", null);
            quickAddRow.set("latitude", null);
            quickAddRow.set("longitude", null);
            quickAddRow.set("zip_code", null);
        }
    }

    private tableDriverCallinsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        if (tableRow != this.tableDriverCallins.quickAddRow) {
            const cityName = tableRow.findComponentById("textboxCityName") as Textbox;
            if (cityName == null) return;

            cityName.lookupModel = null;
            cityName.field = null;
            cityName.text = this.getCSZ(modelRow);
        }
        const orderId = tableRow.findComponentById("labelOrderId") as Label;
        orderId.addUnderlineWhenMouseOver();
    }

    private tableDriverCallinsOnRowCreate(event: TableRowCreationEvent) {
        if (event.getTableRow() != this.tableDriverCallins.quickAddRow) return;
        this.fillQuickAddRow(event.getTableRow().data);
    }

    private tableDriverCallinsBeforeRowSave(event: TableRowCreationEvent) {
        const modelRow: ModelRow = event.getTableRow().data;

        // Faux "live-update" of the last location on call-in entry
        if (this.textboxLastLocation) {
            this.textboxLastLocation.field = null;
            const callDateTime = modelRow.getDateTime("call_date_time");
            this.getTimezone(modelRow.get("city_name"), modelRow.get("state")).then((timezone: string) => {
                this.textboxLastLocation.text = `${DateUtil.formatDateTime(callDateTime).toUpperCase()} ${timezone} ${this.getCSZ(modelRow)}`
            });
        }

        if (this.hasTemperatures) {
            this.setpointTemp = modelRow.get("setpoint_temp");
            this.returnTemp = modelRow.get("temperature");
        }
    }

    private getCSZ(modelRow: ModelRow): string {
        return `${modelRow.get("city_name")}, ${modelRow.get("state", "")} ${modelRow.get("zip_code", "")}`;
    }

    private async getTimezone(name: string, state_id: string): Promise<string> {
        if (!StringUtil.isEmptyString(name) && !StringUtil.isEmptyString(state_id)) {
            const res = await Api.search("common/city", {name, state_id});
            if (res.data && res.data.length > 0) {
                let timezoneId = res.data[0].timezone_id;
                if (timezoneId.charAt(1) === 'D') {
                    timezoneId = timezoneId.charAt(0) + 'S' + timezoneId.slice(2);
                }
                return timezoneId;
            }
            return res.data[0].timezone_id;
        }
        return null;
    }

    private orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }
}
